import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/app/providers/useAuth";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { propertyService } from "@/features/properties/model/propertyServices";
import type { PropertyApiResponse } from "@/features/properties/model/propertiesTypes";
import { toastService } from "@/shared/toast";

type PropertyCard = {
  id: string;
  fullAddress: string;
  name: string;
  cityLine: string;
  county: string;
  type: "Residential" | "Commercial";
  price: string;
  status: string;
  date?: string;
  imageUrl: string;
};

const fallbackImages = [
  "https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "https://images.pexels.com/photos/416320/pexels-photo-416320.jpeg?auto=compress&cs=tinysrgb&w=1200",
  "https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=1200",
];

const toQueryParam = (accountId: string): string => {
  const sp = new URLSearchParams();
  sp.set("account_id", accountId);
  return sp.toString();
};

const formatCurrency = (value: number | null | undefined): string => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "-";
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

const normalizeType = (type: string | null | undefined): "Residential" | "Commercial" => {
  return type?.toLowerCase().includes("commercial") ? "Commercial" : "Residential";
};

const toStatusLabel = (property: PropertyApiResponse): string => {
  if (property.protest_status?.trim()) {
    return property.protest_status.replace(/_/g, " ");
  }
  if (property.status?.trim()) {
    return property.status.replace(/_/g, " ");
  }
  return "No status";
};

const toDateLabel = (isoDate: string | null | undefined): string | undefined => {
  if (!isoDate) {
    return undefined;
  }
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const mapPropertyToCard = (property: PropertyApiResponse, index: number): PropertyCard => {
  const address = property.address;
  const status = toStatusLabel(property);
  const cityLine = [address?.city, address?.state, address?.zip].filter(Boolean).join(", ");
  const fallbackImage =
    fallbackImages[index % fallbackImages.length] ??
    "https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=1200";

  return {
    id: property.property_id,
    fullAddress: property.address?.full_address || "",
    name: property.name || address?.line1 || address?.street || "Property",
    cityLine: cityLine || address?.full_address || "-",
    county: address?.county || "-",
    type: normalizeType(property.property_user_type || property.property_type),
    price: formatCurrency(property.estimated_value ?? property.tax_info?.market_value ?? null),
    status,
    date: toDateLabel(property.updated_at),
    imageUrl: fallbackImage,
  };
};

export const useSettingsPropertiesViewModel = () => {
  const [searchText, setSearchText] = useState("");
  const [mailingTargetId, setMailingTargetId] = useState<string | null>(null);
  const [deletingTargetId, setDeletingTargetId] = useState<string | null>(null);
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const accountId = user?.id ? String(user.id) : "";

  const { data, isLoading, error } = useQuery({
    queryKey: [QUERY_KEYS.PROPERTIES, accountId],
    queryFn: () => propertyService.getAll(toQueryParam(accountId)),
    enabled: isAuthenticated && !!accountId,
  });

  const properties = useMemo(() => {
    const cards = (data || []).map(mapPropertyToCard);
    const needle = searchText.trim().toLowerCase();
    if (!needle) {
      return cards;
    }
    return cards.filter((item) => {
      return (
        item.name.toLowerCase().includes(needle) ||
        item.cityLine.toLowerCase().includes(needle) ||
        item.county.toLowerCase().includes(needle)
      );
    });
  }, [data, searchText]);

  const setAsMailingAddress = useCallback(
    async (property: PropertyCard) => {
      if (!user?.id) {
        toastService.show("Please login to continue.", "error");
        return;
      }

      if (!property.fullAddress.trim()) {
        toastService.show("Property address not found.", "error");
        return;
      }

      const shouldContinue = window.confirm(
        "This will update your profile with this property's address as your mailing address. Continue?"
      );
      if (!shouldContinue) return;

      try {
        setMailingTargetId(property.id);
        const response = await propertyService.setMailingAddressOnProfile(property.fullAddress);

        if (response.success || (response as { status?: string }).status === "success") {
          toastService.show("Mailing address updated successfully!", "success");
        } else {
          toastService.show(response.message || "Failed to update mailing address.", "error");
        }
      } catch {
        toastService.show("Failed to update mailing address. Please try again.", "error");
      } finally {
        setMailingTargetId(null);
      }
    },
    [user?.id]
  );

  const deleteProperty = useCallback(
    async (property: PropertyCard) => {
      if (!property.id) {
        toastService.show("Property ID not found.", "error");
        return;
      }

      const shouldContinue = window.confirm(
        "Are you sure you want to delete this property? This action cannot be undone."
      );
      if (!shouldContinue) return;

      try {
        setDeletingTargetId(property.id);
        const response = await propertyService.delete({ property_id: property.id });

        if (response.success || (response as { status?: string }).status === "success") {
          toastService.show("Property deleted successfully.", "success");
          await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PROPERTIES, accountId] });
          return;
        }

        toastService.show(response.message || "Failed to delete property.", "error");
      } catch {
        toastService.show("Failed to delete property. Please try again.", "error");
      } finally {
        setDeletingTargetId(null);
      }
    },
    [accountId, queryClient]
  );

  return {
    searchText,
    setSearchText,
    properties,
    isLoading,
    error,
    mailingTargetId,
    deletingTargetId,
    setAsMailingAddress,
    deleteProperty,
  };
};
